import { useVendorStore } from '@/store/vendorStore';
import isMobile from '@/utils/checkMobile';

export type NicepayParams = Record<string, string | number>;

export interface NicepayOptions {
    popup?: boolean;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
}

export interface BillingCallbackResponse {
    ResultCode: string;
    ResultMsg: string;
    BID?: string;
    AuthDate?: string;
    CardCode?: string;
    CardName?: string;
    TID?: string;
    CardCl?: string;
    AcquCardCode?: string;
    AcquCardName?: string;
    CardNo?: string;
    BuyerEmail?: string;
}

export interface NicepayResult {
    ResultCode: string;
    ResultMsg: string;
    AuthToken?: string;
    TxTid?: string;
    MID?: string;
    Moid?: string;
    BillKey?: string;
    CardName?: string;
    CardNo?: string;
}

/**
 * NICEPAY 인증창을 오픈합니다.
 * - 우선 NICEPAY 전용 JS 함수(window.NICEPAY.requestAuth or goPay)를 사용합니다.
 * - 만약 스크립트가 없거나 오류가 발생하면 fallback으로 form POST(/gwPayment.jsp)를 사용합니다.
 */
export async function openNicepayAuth(params: NicepayParams, options?: NicepayOptions): Promise<void> {
    try {
        // NICEPAY 콜백 함수 등록
        setupNicepayCallback(options);

        // 1) NICEPAY 전용 함수 우선
        if (window.NICEPAY?.requestAuth) {
            window.NICEPAY.requestAuth(params);
            return;
        }
        if (typeof window.goPay === 'function') {
            // nicepay-pgweb.js가 제공하는 goPay(form) 방식
            const form = buildNicepayForm(params);
            window.goPay(form);
            return;
        }

        // 2) fallback: form POST 직접 전송 (/gwPayment.jsp)
        const form = buildNicepayForm(params);
        form.action = 'https://pg-web.nicepay.co.kr/v3/gwPayment.jsp';

        const isMobileDevice = isMobile();

        if (options?.popup && !isMobileDevice) {
            // PC에서만 팝업으로 열기
            try {
                const popupOptions = 'width=500,height=600,scrollbars=yes';
                const popup = window.open('', 'nicepay_popup', popupOptions);
                if (popup) {
                    form.target = 'nicepay_popup';
                }
            } catch (e) {
                console.warn('Popup blocked, fallback to redirect');
            }
        } else if (isMobileDevice) {
            // 모바일에서는 현재 창에서 전체화면으로 이동
            // form.target을 설정하지 않아서 현재 창에서 열림
            console.log('Mobile device detected: opening Nicepay in full screen');
        }
        // PC에서 popup 옵션이 false인 경우에도 현재 창에서 열림 (기본 동작)

        form.submit();

        setTimeout(() => {
            document.body.removeChild(form);
        }, 1000);
    } catch (error) {
        console.error('NICEPAY auth error:', error);
        throw new Error('결제 인증창을 열 수 없습니다. 다시 시도해주세요.');
    }
}

/**
 * NICEPAY 콜백 함수 설정
 */
function setupNicepayCallback(options?: NicepayOptions): void {
    // 콘솔 메시지를 가로채는 방법으로 NICEPAY 데이터 캐치 시도
    const originalConsoleLog = console.log;
    let lastNicepayData: any = null;

    console.log = function (...args: any[]) {
        // 원래 console.log 먼저 호출
        originalConsoleLog.apply(console, args);

        // NICEPAY가 출력하는 "[Call POST Message]" 메시지를 가로채기
        const message = args.join(' ');
        if (message.includes('[Call POST Message]') && message.includes('code')) {
            try {
                // JSON 데이터 추출 - 더 정확한 패턴 매칭
                const jsonMatch = message.match(/\{[^}]*"code"[^}]*\}/);
                if (jsonMatch) {
                    const jsonStr = jsonMatch[0];
                    lastNicepayData = JSON.parse(jsonStr);
                    originalConsoleLog('🎯 콘솔에서 NICEPAY 데이터 캐치 성공:', lastNicepayData);
                } else {
                    // 더 유연한 JSON 추출 시도
                    const startIdx = message.indexOf('{');
                    const endIdx = message.lastIndexOf('}');
                    if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
                        const jsonStr = message.substring(startIdx, endIdx + 1);
                        lastNicepayData = JSON.parse(jsonStr);
                        originalConsoleLog('🎯 콘솔에서 NICEPAY 데이터 캐치 성공 (유연한 매칭):', lastNicepayData);
                    }
                }
            } catch (e) {
                originalConsoleLog('❌ NICEPAY 데이터 파싱 오류:', e, 'message:', message);
            }
        }
    };

    // NICEPAY가 인증 완료 후 호출하는 전역 함수
    window.nicepaySubmit = async function (...args: any[]) {
        try {
            // NICEPAY에서 전달하는 실제 데이터 구조 분석
            let nicepayData: any = null;

            // 1. 콘솔에서 캐치한 데이터 우선 사용 (가장 정확한 데이터)
            if (lastNicepayData) {
                nicepayData = lastNicepayData;
                lastNicepayData = null; // 사용 후 클리어
            }

            // 2. 파라미터로 전달된 데이터 확인 (fallback)
            else if (args.length > 0) {
                for (const arg of args) {
                    if (arg && typeof arg === 'object') {
                        nicepayData = arg;
                        break;
                    } else if (typeof arg === 'string') {
                        try {
                            nicepayData = JSON.parse(arg);
                            break;
                        } catch {
                            continue;
                        }
                    }
                }
            }

            // 3. Window 전역 변수에서 NICEPAY 데이터 확인
            if (!nicepayData) {
                const windowAny = window as any;
                const possibleKeys = ['nicepayResult', 'NICEPAY_RESULT', 'authResult', 'paymentResult'];

                for (const key of possibleKeys) {
                    if (windowAny[key]) {
                        nicepayData = windowAny[key];
                        break;
                    }
                }
            }

            // 4. URL 파라미터에서 데이터 확인
            if (!nicepayData) {
                const urlParams = new URLSearchParams(window.location.search);
                const urlData: any = {};
                urlParams.forEach((value, key) => {
                    urlData[key] = value;
                });

                if (Object.keys(urlData).length > 0) {
                    nicepayData = urlData;
                }
            }

            // 5. DOM 요소에서 데이터 확인 (hidden input 등)
            if (!nicepayData) {
                const hiddenInputs = document.querySelectorAll('input[type="hidden"]');
                const domData: any = {};

                hiddenInputs.forEach((input: any) => {
                    if (input.name && input.value) {
                        domData[input.name] = input.value;
                    }
                });

                if (Object.keys(domData).length > 0) {
                    nicepayData = domData;
                }
            }

            // 데이터가 없는 경우 처리
            if (!nicepayData || Object.keys(nicepayData).length === 0) {
                console.error('NICEPAY 콜백 데이터가 없습니다.');
                const error = new Error('인증 결과를 받을 수 없습니다.');
                options?.onError?.(error);
                return;
            }

            // NICEPAY 응답 구조에 따른 처리
            let resultCode: string;
            let resultData: any;

            if (nicepayData.code !== undefined) {
                // {"code":"0","message":"success","result":{...}} 형태 (콘솔에서 캐치한 데이터)
                resultCode = nicepayData.code;
                resultData = nicepayData.result || nicepayData;
            } else if (nicepayData.AuthResultCode !== undefined) {
                // DOM에서 가져온 인증 완료 데이터 형태
                resultCode = nicepayData.AuthResultCode === '0000' ? '0' : nicepayData.AuthResultCode;
                resultData = nicepayData;
            } else if (nicepayData.ResultCode !== undefined) {
                // 기존 {ResultCode: "0000", ...} 형태
                resultCode = nicepayData.ResultCode;
                resultData = nicepayData;
            } else if (nicepayData.MID !== undefined && nicepayData.AuthToken !== undefined) {
                // NICEPAY 요청/응답 파라미터가 혼합된 형태 (DOM에서 수집된 경우)
                // AuthResultCode가 0000이면 성공으로 처리
                resultCode = nicepayData.AuthResultCode === '0000' ? '0' : '9999';
                resultData = nicepayData;
            } else {
                console.error('알 수 없는 NICEPAY 응답 형식:', nicepayData);

                // 데이터가 있지만 형식을 인식못하는 경우, 성공으로 가정하고 처리 시도
                if (Object.keys(nicepayData).length > 0) {
                    resultCode = '0';
                    resultData = nicepayData;
                } else {
                    const error = new Error('인증 결과 형식이 올바르지 않습니다.');
                    options?.onError?.(error);
                    return;
                }
            }

            if (resultCode === '0' || resultCode === '0000') {
                // 성공 - 백엔드 콜백 API 호출

                // 백엔드 auth-callback API 호출
                const callbackResponse = await callBillingAuthCallback(resultData, nicepayData);

                if (callbackResponse.ResultCode === 'F100') {
                    options?.onSuccess?.(callbackResponse);
                } else {
                    console.error('백엔드 처리 실패:', callbackResponse.ResultMsg);
                    const error = new Error(callbackResponse.ResultMsg || '카드 등록 처리에 실패했습니다.');
                    options?.onError?.(error);
                }
                // 어떤 결과이든 NICEPAY 레이어 정리 시도
                cleanupNicepayUiArtifacts();
            } else {
                // 실패
                const errorMsg = nicepayData.message || nicepayData.ResultMsg || '인증에 실패했습니다.';
                console.error('NICEPAY 인증 실패:', errorMsg);
                const error = new Error(errorMsg);
                options?.onError?.(error);
                // 실패 시에도 레이어 정리
                cleanupNicepayUiArtifacts();
            }
        } catch (error) {
            console.error('NICEPAY 콜백 처리 중 오류:', error);
            options?.onError?.(error);
            // 예외 발생 시에도 정리
            cleanupNicepayUiArtifacts();
        }
    };

    // 팝업 닫기 콜백 (선택사항)
    window.nicepayClose = function () {
        // 팝업이 닫혔을 때 추가 처리가 필요하면 여기에 구현
    };
}

/**
 * NICEPAY 레이어/팝업/임시 폼 정리
 */
function cleanupNicepayUiArtifacts(): void {
    try {
        const selectors = [
            '#nice_layer',
            '#bg_layer',
            '#np_direct_layer',
            '.np-direct-layer',
            'iframe[name="nicepay_popup"]',
        ];
        selectors.forEach((sel) => {
            document.querySelectorAll(sel).forEach((el) => {
                try {
                    el.remove();
                } catch {}
            });
        });

        // 고정 포지션의 높은 z-index 레이어 중 'Please, wait' 텍스트 포함 요소 제거
        const allNodes = Array.from(document.querySelectorAll<HTMLElement>('body *'));
        allNodes.forEach((el) => {
            try {
                const style = getComputedStyle(el);
                if (style.position === 'fixed' && Number(style.zIndex || 0) >= 999) {
                    const text = (el.innerText || '').toLowerCase();
                    if (text.includes('please') && text.includes('wait')) {
                        el.remove();
                    }
                }
            } catch {}
        });

        // 딜레이 후 한 번 더 시도 (비동기 렌더 잔여물 대비)
        setTimeout(() => {
            try {
                selectors.forEach((sel) => document.querySelectorAll(sel).forEach((el) => el.remove()));
            } catch {}
        }, 150);
    } catch (e) {
        console.warn('NICEPAY UI 정리 중 경고:', e);
    }
}

/**
 * 백엔드 billing/auth-callback API 호출
 */
async function callBillingAuthCallback(resultData: any, originalData: any): Promise<BillingCallbackResponse> {
    const { post } = await import('../helpers/api.helper');
    const config = await import('../config/config');
    const { useUserStore } = await import('../store/userStore');

    // NICEPAY 결과를 백엔드 API 형식에 맞게 변환
    const { vendorId } = useVendorStore.getState();
    const loggedInUserId = vendorId || '';
    if (!loggedInUserId) {
        throw new Error('로그인이 필요합니다. 사용자 ID가 없습니다.');
    }

    const payload: Record<string, string> = {
        UserId: loggedInUserId,
        // 성공 코드 설정
        AuthResultCode: originalData.code === '0' ? '0000' : originalData.ResultCode || '9999',
        AuthResultMsg: originalData.message || originalData.ResultMsg || 'success',

        // 인증 토큰 및 거래 정보
        AuthToken: resultData.AuthToken || resultData.authToken || '',
        TxTid: resultData.TxTid || resultData.txTid || '',
        MID: resultData.MID || resultData.mid || '',
        Moid: resultData.Moid || resultData.moid || '',

        // 빌링키 정보
        BillKey: resultData.BillKey || resultData.billKey || '',

        // 카드 정보
        CardName: resultData.CardName || resultData.cardName || '',
        CardNo: resultData.CardNo || resultData.cardNo || '',

        // 추가 필드들
        BillAuthYN: resultData.BillAuthYN || resultData.billAuthYN || 'Y',
        Signature: resultData.Signature || resultData.signature || '',
        BillingNextAppURL: resultData.BillingNextAppURL || resultData.billingNextAppURL || '',
    };

    const response = await post(`${config.default.api.matching_api}/billing/auth-callback`, payload);
    return response.data;
}

/**
 * NICEPAY 요청 폼 생성 유틸
 */
function buildNicepayForm(params: NicepayParams): HTMLFormElement {
    const form = document.createElement('form');
    form.method = 'POST';
    form.style.display = 'none';

    Object.entries(params).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
    });

    document.body.appendChild(form);
    return form;
}

// NICEPAY 전역 객체 타입 확장
declare global {
    interface Window {
        NICEPAY?: {
            requestAuth: (params: NicepayParams) => void;
        };
        goPay?: (form: HTMLFormElement) => void;
        nicepaySubmit?: (...args: any[]) => void;
        nicepayClose?: () => void;
    }
}
